import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table
} from 'react-bootstrap';

const CatModulos = () => {
    const { token } = useToken();
    const { showErrorToast } = useToast();
    const navigate = useNavigate();

    const [modulos, setModulos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);

    // Modal
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);

    // Formulario
    const formInicial = {
        idCatModulo: null,
        vDescripcionModulo: '',
        vCodigoModulo: '',
        idCatTipoLicencia: null,
        bIsActive: true
    };
    const [form, setForm] = useState(formInicial);

    useEffect(() => {
        checkPermisos();
    }, []);

    const checkPermisos = async () => {
        const response = await fetch('/api/Administradores/checkPermisos', {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        }).catch((error) => {
            showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            console.log('Hubo un problema con la peticion Fetch:' + error);
        });

        if (response.ok) {
            const responseType = await response.json();
            if (responseType.iResponseType == 1) {
                obtenerModulos();
            } else {
                navigate('/');
            }
        } else {
            navigate('/');
        }
    };

    const obtenerModulos = async () => {
        setCargando(true);
        try {
            const response = await fetch('/api/CatModulos/ObtenerCatModulos', {
                headers: { Authorization: 'Bearer ' + token }
            });

            if (!response.ok) {
                showErrorToast('Error al obtener módulos: ' + response.status);
                return;
            }

            const data = await response.json();

            if (data?.iResponseType !== 1) {
                showErrorToast(data?.vMessage || 'Error al obtener módulos.');
                setModulos([]);
                return;
            }

            // detalles contiene el DataSet restante: { table2: [...] } (table1 fue la fila iResponseType, removida por el controller)
            const detalles = data?.detalles ? JSON.parse(data.detalles) : {};
            const tabla = detalles?.table2;
            if (Array.isArray(tabla)) {
                setModulos(tabla);
            } else {
                setModulos([]);
            }
        } catch (error) {
            showErrorToast('Error al obtener módulos: ' + error);
        } finally {
            setCargando(false);
        }
    };

    const abrirModalNuevo = () => {
        setForm(formInicial);
        setModoEdicion(false);
        setShowModal(true);
    };

    const abrirModalEditar = (modulo) => {
        setForm({
            idCatModulo: modulo.idCatModulo,
            vDescripcionModulo: modulo.vDescripcionModulo,
            vCodigoModulo: modulo.vCodigoModulo ?? '',
            idCatTipoLicencia: modulo.idCatTipoLicencia ?? null,
            bIsActive: modulo.bIsActive
        });
        setModoEdicion(true);
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setForm(formInicial);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value === '' ? null : value
        }));
    };

    const guardar = async () => {
        if (!form.vDescripcionModulo?.trim()) {
            showErrorToast('La descripción del módulo es requerida.');
            return;
        }

        setGuardando(true);
        try {
            const response = await fetch('/api/CatModulos/InsUpdCatModulo', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    idCatModulo: form.idCatModulo ? Number(form.idCatModulo) : null,
                    vDescripcionModulo: form.vDescripcionModulo.trim(),
                    vCodigoModulo: form.vCodigoModulo?.trim().toUpperCase() || null,
                    idCatTipoLicencia: form.idCatTipoLicencia ? Number(form.idCatTipoLicencia) : null,
                    bIsActive: form.bIsActive
                })
            });

            const data = await response.json();

            if (data?.iResponseType === 1) {
                cerrarModal();
                obtenerModulos();
            } else {
                showErrorToast(data?.vMessage || 'Error al guardar módulo.');
            }
        } catch (error) {
            showErrorToast('Error al guardar módulo: ' + error);
        } finally {
            setGuardando(false);
        }
    };

    return (
        <>
            <Card className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <FontAwesomeIcon icon={icon({ name: 'puzzle-piece', style: 'solid' })} className="me-2 text-primary" />
                        Catálogo de Módulos
                    </h5>
                    <Button variant="primary" size="sm" onClick={abrirModalNuevo}>
                        <FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} className="me-1" />
                        Nuevo Módulo
                    </Button>
                </Card.Header>

                <Card.Body>
                    {cargando ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <Table responsive hover size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>#</th>
                                    <th>Descripción</th>
                                    <th>Código</th>
                                    <th>Tipo Licencia</th>
                                    <th>Estado</th>
                                    <th>Creado</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {modulos.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-3">
                                            Sin módulos registrados.
                                        </td>
                                    </tr>
                                ) : (
                                    modulos.map(m => (
                                        <tr key={m.idCatModulo}>
                                            <td>{m.idCatModulo}</td>
                                            <td>{m.vDescripcionModulo}</td>                                            <td>
                                                {m.vCodigoModulo
                                                    ? <code className="text-primary">{m.vCodigoModulo}</code>
                                                    : <span className="text-muted">—</span>
                                                }
                                            </td>                                            <td>{m.vTipoLicencia ?? <span className="text-muted">—</span>}</td>
                                            <td>
                                                <Badge bg={m.bIsActive ? 'success' : 'secondary'}>
                                                    {m.bIsActive ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="text-muted small">
                                                {m.vCreatedUser} {m.dCreatedDate ? `· ${new Date(m.dCreatedDate).toLocaleDateString()}` : ''}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => abrirModalEditar(m)}
                                                >
                                                    <FontAwesomeIcon icon={icon({ name: 'pen', style: 'solid' })} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Modal crear / editar */}
            <Modal show={showModal} onHide={cerrarModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modoEdicion ? 'Editar Módulo' : 'Nuevo Módulo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label>Descripción <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="vDescripcionModulo"
                                value={form.vDescripcionModulo}
                                onChange={handleChange}
                                placeholder="Ej. Módulo Pago Fiado"
                                maxLength={200}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label>Código del módulo <span className="text-muted small">(opcional)</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="vCodigoModulo"
                                value={form.vCodigoModulo}
                                onChange={handleChange}
                                placeholder="Ej. ACCOUNTS_RECEIVABLE"
                                maxLength={50}
                                style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
                            />
                            <Form.Text className="text-muted">
                                Códigos válidos en AkasiaPOS: ACCOUNTS_RECEIVABLE, CASH_REGISTERS,
                                SELL, PRODUCTS, MERCHANDISE, CUSTOMERS
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label>Tipo Licencia <span className="text-muted small">(opcional)</span></Form.Label>
                            <Form.Select
                                name="idCatTipoLicencia"
                                value={form.idCatTipoLicencia ?? ''}
                                onChange={handleChange}
                            >
                                <option value=''>— Sin tipo de licencia —</option>
                                <option value='1'>POS Servidor</option>
                                <option value='2'>POS Caja Adicional</option>
                                <option value='3'>Nube Akasia</option>
                                <option value='4'>POS App Comanda</option>
                                <option value='5'>CON Contabilidad</option>
                                <option value='6'>CON Nomina</option>
                                <option value='7'>Akasia POS Web</option>
                                <option value='8'>Akasia Mart Android</option>
                                <option value='9'>Akasia Mart Desktop</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12}>
                            <Form.Check
                                type="switch"
                                id="bIsActive"
                                name="bIsActive"
                                label="Activo"
                                checked={form.bIsActive}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cerrarModal} disabled={guardando}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={guardar} disabled={guardando}>
                        {guardando
                            ? <><Spinner size="sm" className="me-1" />Guardando...</>
                            : modoEdicion ? 'Actualizar' : 'Crear Módulo'
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CatModulos;
